import { memo } from 'react';

/**
 * MAP ICONS - one set, one weight, one grid.
 *
 * The map's controls grew a glyph at a time: `▩ ⌫ ░ ▽ ⧉ ▭ ╱ ✎ ▶ ▦ ⇅ 🕊 ƒ`
 * - geometric shapes, an emoji, a letter and a box-drawing character, at
 * four different optical weights, because each was chosen on its own and
 * looked fine on its own.
 *
 * This follows the convention the app already has: `COND_ICONS` in
 * `engine/conditions.ts` is a record of SVG path data on a 16-unit grid
 * rendered at one stroke weight by `ConditionIcon`. A second convention
 * would have been the easier thing to write and the worse thing to own.
 *
 * Drawn on a 16×16 grid, stroked rather than filled unless a shape reads
 * better solid, so every icon has the same visual mass whatever it
 * depicts.
 */
export const MAP_ICONS = {
  // ---- modes ----
  play: [{ d: 'M5,3 L13,8 L5,13 Z', fill: true }],
  // A ping: the ripple and the point it opens from.
  ping: [{ d: 'M8,2.5 A5.5,5.5 0 1,1 7.99,2.5 Z' }, { d: 'M8,6.5 A1.5,1.5 0 1,1 7.99,6.5 Z', fill: true }],
  build: [{ d: 'M2.5,9.5 L8,6 L13.5,9.5 L8,13 Z' }, { d: 'M2.5,6.5 L8,3 L13.5,6.5' }],

  // A planted flag: the marker is a thing you put in the ground.
  deploy: [{ d: 'M4.5,14 L4.5,2.5' }, { d: 'M4.5,3 L12.5,5.5 L4.5,8 Z' }],
  // A clapperboard: the board, the hinged slate, two stripes.
  scenes: [
    { d: 'M2.5,7 L13.5,7 L13.5,13.5 L2.5,13.5 Z' },
    { d: 'M2.5,7 L3.5,3.5 L13.5,4.5 L13.5,7' },
    { d: 'M6,3.8 L5.2,7' },
    { d: 'M9.5,4.1 L8.7,7' },
  ],

  // ---- build tools ----
  fill: [{ d: 'M3.5,7 L8,4 L12.5,7 L12.5,11.5 L8,14 L3.5,11.5 Z' }, { d: 'M8,4 L8,9 M3.5,7 L8,9 L12.5,7' }],
  erase: [{ d: 'M3,10 L8,4.5 L13,9 L9.5,13 L5,13 Z' }, { d: 'M5,13 L9.5,13' }],
  zone: [{ d: 'M2.5,10 L8,6.5 L13.5,10 L8,13.5 Z' }, { d: 'M5.5,4 L6.5,5.5 M9.5,3.5 L10.5,5 M7.5,2.5 L8.5,4' }],
  dig: [{ d: 'M2.5,5.5 L5.5,5.5 L7,10 L9,10 L10.5,5.5 L13.5,5.5' }, { d: 'M2.5,5.5 L2.5,3.5 M13.5,5.5 L13.5,3.5' }],
  select: [{ d: 'M3,3 L10,3 L10,10 L3,10 Z', dashed: true }, { d: 'M6,6 L13,6 L13,13 L6,13 Z' }],

  // ---- gestures ----
  shape: [{ d: 'M2.5,4.5 L13.5,4.5 L13.5,11.5 L2.5,11.5 Z' }],
  line: [{ d: 'M3,13 L13,3' }, { d: 'M3,13 L5.5,13 M13,3 L13,5.5' }],
  brush: [{ d: 'M4,12 L10.5,3.5 L12.5,5.5 L6,14 L3,14.5 Z' }],

  // ---- footprint shapes ----
  rect: [{ d: 'M3,5 L13,5 L13,11 L3,11 Z' }],
  ellipse: [{ d: 'M8,4.5 C11.5,4.5 14,6 14,8 C14,10 11.5,11.5 8,11.5 C4.5,11.5 2,10 2,8 C2,6 4.5,4.5 8,4.5 Z' }],
  triangle: [{ d: 'M8,3 L14,13 L2,13 Z' }],
  ngon: [{ d: 'M8,2.5 L13.5,6.5 L11.5,13 L4.5,13 L2.5,6.5 Z' }],
  // Kept though the build bar no longer draws them: Hollow and Float
  // became rounded pills with a state dot in v7.59.0, because they are
  // MODIFIERS to a shape rather than shapes. The glyphs stay in the
  // catalogue for anything that wants them later.
  hollow: [{ d: 'M3,4 L13,4 L13,12 L3,12 Z' }, { d: 'M5.5,6.5 L10.5,6.5 L10.5,9.5 L5.5,9.5 Z' }],

  // ---- toggles ----
  labels: [{ d: 'M2.5,3.5 L9,3.5 L13.5,8 L9,12.5 L2.5,12.5 Z' }, { d: 'M5.5,8 A0.9,0.9 0 1,1 5.5,7.9 Z', fill: true }],
  noTerrain: [{ d: 'M8,2.5 A5.5,5.5 0 1,1 8,13.5 A5.5,5.5 0 1,1 8,2.5 Z' }, { d: 'M4,12 L12,4' }],
  target: [
    { d: 'M8,3 A5,5 0 1,1 8,13 A5,5 0 1,1 8,3 Z' },
    { d: 'M8,1.5 L8,4 M8,12 L8,14.5 M1.5,8 L4,8 M12,8 L14.5,8' },
    { d: 'M8,7 A1,1 0 1,1 8,9 A1,1 0 1,1 8,7 Z', fill: true },
  ],
  float: [{ d: 'M8,2.5 L8,13.5' }, { d: 'M5,5.5 L8,2.5 L11,5.5 M5,10.5 L8,13.5 L11,10.5' }],
  flying: [{ d: 'M2,9 C5,4 7,7 8,7 C9,7 11,4 14,9' }, { d: 'M8,7 L8,12' }],
  legend: [
    { d: 'M8,2.5 A5.5,5.5 0 1,1 8,13.5 A5.5,5.5 0 1,1 8,2.5 Z' },
    { d: 'M6,6.5 A2,2 0 1,1 8,9.5 L8,10' },
    { d: 'M8,12 A0.6,0.6 0 1,1 8,11.9 Z', fill: true },
  ],
  reset: [{ d: 'M13,8 A5,5 0 1,1 11,4.2' }, { d: 'M11,1.5 L11,4.5 L8,4.5' }],
};

/**
 * One icon, at the size the surrounding control wants.
 *
 * `currentColor` throughout, so a button's own colour - hovered, active,
 * disabled - reaches the icon without anything being passed down.
 */
function MapIconImpl({ name, size = 15 }) {
  const paths = MAP_ICONS[name];
  if (!paths) return null;
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flexShrink: 0 }}
    >
      {paths.map((path, i) => (
        <path
          key={i}
          d={path.d}
          fill={path.fill ? 'currentColor' : 'none'}
          stroke={path.fill ? 'none' : 'currentColor'}
          strokeWidth={path.fill ? 0 : 1.4}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={path.dashed ? '2.5 1.8' : undefined}
        />
      ))}
    </svg>
  );
}

export default memo(MapIconImpl);
